import React from 'react';

export enum MarkerTypes {
  User = 'user',
  StoreActive = 'storeActive',
  StoreInActive = 'storeInActive',
  StoreDisabled = 'storeDisabled',
  StoreFavOpen = 'storeFavOpen',
  StoreFavClosed = 'storeFavClosed',
  Destination = 'destination',
  Driver = 'driver',
  /** Large branded teardrop shown for the store the guest has selected. */
  StoreSelectedOpen = 'storeSelectedOpen',
  StoreSelectedClosed = 'storeSelectedClosed',
  /** Branded teardrop with a house, marking the address a delivery is going to. Web only. */
  DeliveryAddress = 'deliveryAddress',
}

export interface IMarkerInput {
  /**
   * Stable identity for the marker. Several restaurants can share one address, and keying
   * a marker on its coordinates alone collapses them into a single pin that overwrites its
   * own treatment as each store is processed. Falls back to coordinates when absent.
   */
  id?: string;
  type: MarkerTypes;
  location: CenterPoint;
  onPress?: () => void;
  /** Accessible name for a pressable marker. Without it the pin is an unnamed button. */
  title?: string;
}

export interface IMarker extends IMarkerInput {
  key: string;
  icon: React.ReactNode;
  preventMarkerPress: boolean;
}

export type MarkersMap = {
  [key: string]: IMarker;
};

type MapEventListener = (...args: any[]) => void;

export interface IUseMapArgs {
  initialZoomAndroid?: number;
  initialZoomIOS?: number;
  initialMarkers?: IMarkerInput[];
  eventListeners?: {
    [key: string]: MapEventListener | Array<MapEventListener>;
  };
  position?: CenterPoint;
  /**
   * Zoom level the map is created at. Web only — native callers frame the map through the
   * camera on `@fhs/maps` instead.
   */
  initialZoom?: number;
  disableControls?: boolean;
  isStaticMap?: boolean;
}

export interface IPanToOptions {
  /**
   * Zoom level to settle on, instead of only raising the zoom to the default when the map is
   * further out than that. Web only — native callers use the camera on `@fhs/maps` instead.
   */
  zoom?: number;
}

export type PanToFn = (
  newPosition: {
    lat?: number | null;
    lng?: number | null;
  } | null,
  options?: IPanToOptions
) => Promise<void>;

export type FitAndCenterFromCoordsFn = (coordsArray: CenterPoint[]) => void;

export type CenterPoint = {
  lat: number;
  lng: number;
};

export interface ILocation extends CenterPoint {
  accuracy?: number;
}

export type IUseMapHook = (args?: IUseMapArgs) => {
  center: CenterPoint;
  createMarker: (args: IMarkerInput) => void;
  /**
   * Takes the listed markers off the map, by the same id they were created with.
   * `createMarker` only ever adds, so a caller whose list has shrunk is the only one that
   * knows a pin is stale. Web only — native callers hand `@fhs/maps` the stores to show and
   * its markers follow that list on their own.
   */
  removeMarkers?: (ids: string[]) => void;
  fitAndCenterFromCoords: FitAndCenterFromCoordsFn;
  map: React.ReactNode;
  panTo: PanToFn;
  zoom: number;
  resetZoom: () => void;
  /**
   * Steps the zoom level by `delta` (positive zooms in), backing the custom +/- map controls.
   * Web only — native callers use the camera on `@fhs/maps` instead.
   */
  zoomBy?: (delta: number) => void;
  isMapReady: boolean;
};
